/**
 * Per-user, cross-device persistence for `DataTable` column layouts
 * (order, widths, visibility).
 *
 * `DataTable` already mirrors these to localStorage so a reload is
 * instant; this hook layers `user_preferences` (migration 101) on top so
 * the layout an operator arranges survives a cleared browser, a new
 * laptop, or the shop-floor kiosk.
 *
 * Both copies carry an "edited at" timestamp and the newer one wins. The
 * server copy can lag behind localStorage (a reload inside the save
 * debounce, a closed tab, a failed request), so letting it win
 * unconditionally would undo the operator's most recent change.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnOrderState, ColumnSizingState, VisibilityState } from '@tanstack/react-table';
import { supabase } from '@/lib/supabase';
import { useOptionalAuth } from '@/app/AuthProvider';

export interface TableLayout {
  order: ColumnOrderState;
  sizing: ColumnSizingState;
  visibility: VisibilityState;
}

interface RemoteLayout {
  layout: Partial<TableLayout>;
  /** `null` for rows saved before timestamps were recorded. */
  updatedAt: number | null;
}

const PREF_KEY_PREFIX = 'table_layout:';

/** localStorage key prefix for when this browser last changed a table's
 *  layout (epoch ms). Absent means "never edited here". */
const LOCAL_UPDATED_AT_PREFIX = 'atlas:dt:layoutAt:v1:';

/** Resizing fires on every pointer move; only persist once the user pauses. */
const SAVE_DEBOUNCE_MS = 600;

const SYSTEM_COLUMN_PREFIX = '__';

function parseRemoteLayout(value: unknown): RemoteLayout | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const layout: Partial<TableLayout> = {};
  if (Array.isArray(v.order) && v.order.every((id) => typeof id === 'string')) {
    layout.order = (v.order as string[]).filter((id) => !id.startsWith(SYSTEM_COLUMN_PREFIX));
  }
  if (isRecordOf(v.sizing, (n) => typeof n === 'number' && Number.isFinite(n))) {
    layout.sizing = v.sizing as ColumnSizingState;
  }
  if (isRecordOf(v.visibility, (b) => typeof b === 'boolean')) {
    layout.visibility = v.visibility as VisibilityState;
  }
  const updatedAt =
    typeof v.updatedAt === 'number' && Number.isFinite(v.updatedAt) ? v.updatedAt : null;
  return { layout, updatedAt };
}

function isRecordOf(value: unknown, check: (v: unknown) => boolean): boolean {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value as Record<string, unknown>).every(check)
  );
}

/** System columns (selection checkbox, row chevrons) are always placed where
 *  the page declares them, and some only appear once the user's role loads,
 *  so they're left out of what gets compared and saved. */
function normalizeLayout(layout: TableLayout): TableLayout {
  return {
    order: layout.order.filter((id) => !id.startsWith(SYSTEM_COLUMN_PREFIX)),
    sizing: layout.sizing,
    visibility: layout.visibility,
  };
}

function readLocalUpdatedAt(key: string | null): number | null {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeLocalUpdatedAt(key: string, value: number | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // Quota exceeded / disabled — best-effort persistence only.
  }
}

/**
 * @param tableId   Stable table identifier; sync is disabled without one.
 * @param layout    The table's current layout state.
 * @param defaults  The layout the table renders with no customisation. A
 *                  user who has never touched a table doesn't get a row.
 * @param apply     Called once with the server copy when it is newer than
 *                  what the table hydrated from localStorage.
 */
export function useSyncedTableLayout({
  tableId,
  layout,
  defaults,
  apply,
}: {
  tableId: string | undefined;
  layout: TableLayout;
  defaults: TableLayout;
  apply: (remote: Partial<TableLayout>) => void;
}) {
  const auth = useOptionalAuth();
  const queryClient = useQueryClient();
  const profileId = auth?.user?.id ?? null;
  const prefKey = tableId ? `${PREF_KEY_PREFIX}${tableId}` : null;
  const localAtKey = tableId ? `${LOCAL_UPDATED_AT_PREFIX}${tableId}` : null;
  const enabled = !!profileId && !!prefKey;
  const queryKey = useMemo(
    () => ['user-preferences', prefKey, profileId] as const,
    [prefKey, profileId],
  );

  const query = useQuery({
    queryKey,
    enabled,
    staleTime: Infinity,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('value')
        .eq('profile_id', profileId!)
        .eq('key', prefKey!)
        .maybeSingle();
      if (error) throw error;
      return parseRemoteLayout(data?.value);
    },
  });

  const layoutJson = JSON.stringify(normalizeLayout(layout));
  const defaultsJson = JSON.stringify(normalizeLayout(defaults));

  // Stamp every layout change made in this browser so a reload can tell
  // whether localStorage or the server holds the newer arrangement. The
  // change that results from applying the server copy inherits the
  // server's timestamp instead of "now". Declared before the hydration
  // effect so the stamp for an apply is consumed on the following render.
  const prevLayoutRef = useRef({ key: localAtKey, json: layoutJson });
  const pendingApplyAtRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevLayoutRef.current;
    const appliedAt = pendingApplyAtRef.current;
    pendingApplyAtRef.current = undefined;
    prevLayoutRef.current = { key: localAtKey, json: layoutJson };
    if (!localAtKey || prev.key !== localAtKey || prev.json === layoutJson) return;
    writeLocalUpdatedAt(localAtKey, appliedAt !== undefined ? appliedAt : Date.now());
  });

  // Which pref key the initial fetch has settled for. We never write
  // before then, or a stale local layout could clobber the newer one
  // saved from another device.
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);
  const hydrated = enabled && hydratedKey === prefKey;
  // What the server is known to hold (or would hold, for a user with no
  // row yet), as JSON.
  const lastSyncedRef = useRef<string | null>(null);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (!enabled || hydrated) return;
    if (query.isError) {
      // Leave the table on its localStorage layout and stop trying.
      lastSyncedRef.current = layoutJson;
      setHydratedKey(prefKey);
      return;
    }
    if (!query.isSuccess) return;
    const remote = query.data;
    if (!remote) {
      // No server row yet: anything that differs from defaults (e.g. a
      // layout saved to localStorage before cross-device sync existed)
      // gets uploaded by the save effect below.
      lastSyncedRef.current = defaultsJson;
    } else {
      const remoteJson = JSON.stringify(
        normalizeLayout({ ...JSON.parse(defaultsJson), ...remote.layout } as TableLayout),
      );
      lastSyncedRef.current = remoteJson;
      // A browser that has never edited this table (fresh device, cleared
      // storage) takes the server copy. Otherwise only a strictly newer
      // server copy replaces what's on screen; an older one is overwritten
      // by the save effect below.
      const localAt = readLocalUpdatedAt(localAtKey);
      const remoteIsNewer =
        localAt === null || (remote.updatedAt !== null && remote.updatedAt > localAt);
      if (remoteIsNewer && remoteJson !== layoutJson) {
        pendingApplyAtRef.current = remote.updatedAt;
        applyRef.current(remote.layout);
      }
    }
    setHydratedKey(prefKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hydrated, prefKey, query.isSuccess, query.isError, query.data]);

  const saveRef = useRef<(snapshot: string) => void>(() => {});
  saveRef.current = (snapshot: string) => {
    if (!profileId || !prefKey) return;
    const updatedAt = readLocalUpdatedAt(localAtKey) ?? Date.now();
    const value = { ...(JSON.parse(snapshot) as TableLayout), updatedAt };
    lastSyncedRef.current = snapshot;
    void supabase
      .from('user_preferences')
      .upsert({ profile_id: profileId, key: prefKey, value }, { onConflict: 'profile_id,key' })
      .then(({ error }) => {
        if (error) {
          if (lastSyncedRef.current === snapshot) lastSyncedRef.current = null;
          return;
        }
        // Keep the cached copy current so navigating away and back doesn't
        // hydrate from the layout fetched when the page first loaded.
        queryClient.setQueryData<RemoteLayout | null>(queryKey, parseRemoteLayout(value));
      });
  };

  const pendingSaveRef = useRef<string | null>(null);
  useEffect(() => {
    pendingSaveRef.current = null;
    if (!hydrated) return;
    if (layoutJson === lastSyncedRef.current) return;
    pendingSaveRef.current = layoutJson;
    const handle = window.setTimeout(() => {
      pendingSaveRef.current = null;
      saveRef.current(layoutJson);
    }, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [hydrated, layoutJson]);

  // Don't drop a debounced save when the table unmounts (clicking into a
  // row's detail page) or the tab is closed / reloaded mid-debounce.
  useEffect(() => {
    const flush = () => {
      const snapshot = pendingSaveRef.current;
      if (snapshot === null) return;
      pendingSaveRef.current = null;
      saveRef.current(snapshot);
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);
}
